import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { CheckboxInput, Heading3, TextInput } from '@awarego-ui/components';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

import { TextDefault } from '../../../../../components/src';
import { useStores } from '../../../hooks/use-stores';
import { DEFAULT_DEPARTMENT_REPORT_LOOKBACK_DAYS } from './ReportFormFieldDefs';

const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreCase: true,
});

function ReportRecipientsFormFragment({
    reportsForm,
    editingReport,
    inputHasError,
    enableTags = false,
    companyId,
}) {
    const { reportsStore } = useStores();
    const reportType = reportsForm.$('reportType')?.value || '';
    const isDepartmentManager = reportType === 'department-manager';
    const tagField = useMemo(() => {
        if (!enableTags && !isDepartmentManager) return null;
        try {
            return reportsForm.$('reportTags');
        } catch (err) {
            return null;
        }
    }, [enableTags, isDepartmentManager, reportsForm]);

    // Options: loaded tags from store + any from field.extra (user-typed). Store is the source of truth so autocomplete updates when loadReportTags completes.
    const tagOptions = useMemo(() => {
        if (!(enableTags || isDepartmentManager) || !tagField) return [];
        const fromStore = Array.isArray(reportsStore.reportTags)
            ? reportsStore.reportTags
            : [];
        const fromField = Array.isArray(tagField.extra) ? tagField.extra : [];
        return Array.from(
            new Set(
                [...fromStore, ...fromField].filter(
                    (t) => typeof t === 'string' && t.trim().length > 0
                )
            )
        ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [enableTags, isDepartmentManager, tagField, reportsStore.reportTags]);
    const selectedTags =
        (enableTags || isDepartmentManager) &&
        tagField &&
        Array.isArray(tagField.value)
            ? tagField.value
            : [];

    const normalizeTags = (tags) =>
        Array.from(
            new Set(
                tags
                    .filter((tag) => typeof tag === 'string')
                    .map((tag) => tag.trim())
                    .filter((tag) => tag.length > 0)
            )
        );

    useEffect(() => {
        if (editingReport) {
            const recipients = editingReport.recipients;
            reportsForm
                .$('recipientsEmails')
                .set(
                    Array.isArray(recipients)
                        ? recipients.join(', ')
                        : recipients ?? ''
                );
            reportsForm
                .$('customEmailSubject')
                .set(editingReport.customEmailSubject);
            if (reportsForm.has('reportDays')) {
                const rd =
                    editingReport.reportDays ??
                    editingReport.definition?.reportDays;
                reportsForm
                    .$('reportDays')
                    .set(
                        rd != null && rd !== ''
                            ? String(rd)
                            : DEFAULT_DEPARTMENT_REPORT_LOOKBACK_DAYS
                    );
            }
            if (reportsForm.has('dryRun')) {
                reportsForm
                    .$('dryRun')
                    .set(!!(editingReport.dryRun || editingReport.definition?.dryRun));
            }
            if (reportsForm.has('reportLocale')) {
                const rl =
                    editingReport.reportLocale ??
                    editingReport.definition?.locale;
                reportsForm
                    .$('reportLocale')
                    .set(rl && String(rl).trim() ? String(rl).trim() : 'auto');
            }
            if (enableTags && tagField) {
                const editingTags =
                    (Array.isArray(editingReport.tags) && editingReport.tags) ||
                    editingReport.definition?.tags ||
                    editingReport.reportTags ||
                    [];
                if (Array.isArray(editingTags) && editingTags.length > 0) {
                    tagField.set(editingTags);
                    tagField.set(
                        'extra',
                        Array.from(
                            new Set([...(tagField.extra || []), ...editingTags])
                        ).sort((a, b) =>
                            a.localeCompare(b, undefined, {
                                sensitivity: 'base',
                            })
                        )
                    );
                }
            }
            reportsForm.validate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingReport, enableTags, tagField]);

    useEffect(() => {
        if (!isDepartmentManager || !companyId) return;
        reportsStore.loadDepartmentReportLanguages(companyId);
    }, [isDepartmentManager, companyId, reportsStore]);

    useEffect(() => {
        if (!isDepartmentManager || !reportsForm.has('reportLocale')) return;
        const opts = reportsStore.departmentReportLocaleOptions;
        if (!opts || opts.length === 0) return;
        const extra = opts.map((o) => ({ label: o.label, value: o.code }));
        reportsForm.$('reportLocale').set('extra', extra);
        const codes = new Set(opts.map((o) => o.code));
        const cur = reportsForm.$('reportLocale').value;
        if (cur && !codes.has(cur)) {
            reportsForm.$('reportLocale').set(opts[0]?.code || 'auto');
        }
    }, [
        isDepartmentManager,
        reportsStore.departmentReportLocaleOptions,
        reportsForm,
    ]);

    return (
        <>
            {!isDepartmentManager && (
                <>
                    <Heading3 mb={24} displayBlock>
                        Send to email (optional)
                    </Heading3>
                    <TextInput
                        error={inputHasError('recipientsEmails')}
                        margin={'dense'}
                        stretched
                        variant={'outlined'}
                        type={'email'}
                        helperText={
                            inputHasError('recipientsEmails')
                                ? 'Please enter valid email address(es)'
                                : undefined
                        }
                        {...reportsForm.$('recipientsEmails').bind()}
                    />
                </>
            )}
            <TextInput
                margin={'dense'}
                stretched
                variant={'outlined'}
                {...reportsForm.$('customEmailSubject').bind()}
            />
            {reportsForm.has('dryRun') && (
                <>
                    <CheckboxInput
                        disableRipple
                        {...reportsForm.$('dryRun').bind()}
                    />
                    <TextDefault lighter>
                        Generate the report files, but do not email them until
                        you choose Send now.
                    </TextDefault>
                </>
            )}
            {isDepartmentManager && reportsForm.has('reportDays') && (
                <TextInput
                    margin={'dense'}
                    stretched
                    variant={'outlined'}
                    type="number"
                    inputProps={{ min: 1, max: 365 }}
                    error={inputHasError('reportDays')}
                    helperText={
                        inputHasError('reportDays')
                            ? 'Enter a number from 1 to 365, or leave empty.'
                            : 'Active campaigns are always included. Also include completed trainings and assessments that ended within this many days (1–365).'
                    }
                    {...reportsForm.$('reportDays').bind()}
                />
            )}
            {isDepartmentManager && reportsForm.has('reportLocale') && (
                <TextInput
                    select
                    margin={'dense'}
                    stretched
                    variant={'outlined'}
                    {...reportsForm.$('reportLocale').bind()}
                >
                    {reportsForm
                        .$('reportLocale')
                        .extra.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                </TextInput>
            )}
            {(enableTags || isDepartmentManager) && tagField && (
                <>
                    <Heading3 mt={32} mb={16} displayBlock>
                        {isDepartmentManager
                            ? 'Leader tags: send only to users who have all selected tags on a list, and only when there is reportable data for that list (required)'
                            : 'For each employee list, send to the person that has the following tags in that list (optional)'}
                    </Heading3>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={tagOptions}
                        value={selectedTags}
                        filterOptions={filterOptions}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
                        PopperProps={{
                            style: { zIndex: 9999 },
                        }}
                        onChange={(event, newValue) => {
                            if (!tagField) return;
                            const normalized = normalizeTags(newValue);
                            const mergedOptions = Array.from(
                                new Set([
                                    ...(tagField.extra || []),
                                    ...normalized,
                                ])
                            ).sort((a, b) =>
                                a.localeCompare(b, undefined, {
                                    sensitivity: 'base',
                                })
                            );
                            tagField.set('extra', mergedOptions);
                            tagField.set(normalized);
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={isDepartmentManager && inputHasError('reportTags')}
                                helperText={
                                    isDepartmentManager && inputHasError('reportTags')
                                        ? 'Please select at least one leader tag'
                                        : undefined
                                }
                                placeholder={
                                    selectedTags.length === 0
                                        ? 'Type to search or add tags'
                                        : undefined
                                }
                                InputLabelProps={{ shrink: false }}
                            />
                        )}
                        sx={{
                            '& .MuiAutocomplete-inputRoot': {
                                paddingTop: '6px !important',
                                paddingBottom: '6px !important',
                            },
                            '& .MuiInputBase-root .MuiAutocomplete-input': {
                                fontSize: '13px',
                            },
                        }}
                        ListboxProps={{
                            style: { maxHeight: 320, overflow: 'auto' },
                        }}
                        noOptionsText={
                            tagOptions.length === 0
                                ? 'No tags available'
                                : 'No matches'
                        }
                        filterSelectedOptions
                        clearOnBlur={false}
                        selectOnFocus
                        handleHomeEndKeys
                    />
                    <TextDefault mt={14} lighter>Type to search existing tags or press Enter to add a new one.</TextDefault>
                </>
            )}
        </>
    );
}

export default observer(ReportRecipientsFormFragment);
